import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Badge, Button, Card, Form, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";

import {
  checkEventRegistrationStatus,
  getEvent,
  getEventSummary,
  listEventRegistrations,
} from "./services/api";

// Statuses that warrant a re-check against Stripe. Anything in this set is
// "not yet finalized": pending, processing (Pix/Boleto waiting to clear), or
// the requires_action state Stripe maps to pending in our vocabulary.
const RECHECKABLE_STATUSES = new Set(["pending", "processing"]);

const STATUS_OPTIONS = [
  { value: "", label: "Todos" },
  { value: "pending", label: "Pendente" },
  { value: "processing", label: "Processando" },
  { value: "paid", label: "Pago" },
  { value: "free", label: "Gratuito" },
  { value: "failed", label: "Falhou" },
  { value: "canceled", label: "Cancelado" },
  { value: "refunded", label: "Reembolsado" },
];

function formatBRL(cents, currency = "BRL") {
  if (cents == null) return "—";
  try {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency,
    }).format(cents / 100);
  } catch (_e) {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("pt-BR", {
      dateStyle: "short",
      timeStyle: "short",
    }).format(new Date(iso));
  } catch (_e) {
    return iso;
  }
}

function paymentBadge(status) {
  const map = {
    paid: "bg-emerald-100 text-emerald-800 border-emerald-200",
    free: "bg-emerald-100 text-emerald-800 border-emerald-200",
    pending: "bg-amber-100 text-amber-800 border-amber-200",
    processing: "bg-blue-100 text-blue-800 border-blue-200",
    failed: "bg-rose-100 text-rose-800 border-rose-200",
    canceled: "bg-slate-100 text-slate-600 border-slate-200",
    refunded: "bg-slate-100 text-slate-600 border-slate-200",
  };
  const label = {
    paid: "Pago",
    free: "Gratuito",
    pending: "Pendente",
    processing: "Processando",
    failed: "Falhou",
    canceled: "Cancelado",
    refunded: "Reembolsado",
  };
  const cls = map[status] || map.pending;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-semibold uppercase tracking-wide ${cls}`}
    >
      {label[status] || status || "—"}
    </span>
  );
}

export default function EventRegistrations() {
  const navigate = useNavigate();
  const { eventId } = useParams();

  const [event, setEvent] = useState(null);
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [filterSearch, setFilterSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [loading, setLoading] = useState(true);
  // Set of registration ids currently being re-checked. Tracking by id (vs a
  // single boolean) so the per-row spinner is independent from the bulk
  // operation and rows don't all spin together.
  const [checkingIds, setCheckingIds] = useState(() => new Set());
  const [bulkChecking, setBulkChecking] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });

  async function loadAll() {
    setLoading(true);
    const [evResp, sumResp, regsResp] = await Promise.all([
      getEvent(eventId),
      getEventSummary(eventId),
      listEventRegistrations(eventId, {
        status: filterStatus || undefined,
        search: filterSearch || undefined,
        limit: 200,
      }),
    ]);
    if (evResp instanceof Error) toast.error(evResp.message);
    else setEvent(evResp.data);
    if (sumResp instanceof Error) toast.error(sumResp.message);
    else setSummary(sumResp.data);
    if (regsResp instanceof Error) toast.error(regsResp.message);
    else setRows((regsResp.data || []).filter((r) => !r.count));
    setLoading(false);
  }

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  function handleFilter() {
    loadAll();
  }

  function handleClear() {
    setFilterSearch("");
    setFilterStatus("");
    setTimeout(() => loadAll(), 0);
  }

  /**
   * Calls the admin check-status endpoint for one registration. On success
   * we patch only that row in `rows` (and refresh `summary`) so we don't
   * collapse the table back to a full reload — keeps the user's scroll
   * position intact. Returns the updated payload (or null on error) so the
   * bulk handler can aggregate results.
   */
  async function checkOne(registration, { silent = false } = {}) {
    if (!registration?.id) return null;
    setCheckingIds((prev) => {
      const next = new Set(prev);
      next.add(registration.id);
      return next;
    });
    try {
      const response = await checkEventRegistrationStatus(registration.id);
      if (response instanceof Error) {
        if (!silent) toast.error(response.message);
        return null;
      }
      const updated = response.data;
      setRows((prev) =>
        prev.map((r) =>
          r.id === registration.id
            ? {
                ...r,
                payment_status: updated.payment_status,
                payment_method: updated.payment_method ?? r.payment_method,
                paid_at: updated.paid_at ?? r.paid_at,
              }
            : r
        )
      );
      if (!silent) {
        if (updated.status_changed) {
          toast.success(
            `Status atualizado: ${updated.previous_status} → ${updated.current_status}`
          );
        } else {
          toast.info(`Sem alterações (status atual: ${updated.current_status})`);
        }
      }
      return updated;
    } catch (error) {
      if (!silent) toast.error(`Erro ao verificar: ${error.message}`);
      return null;
    } finally {
      setCheckingIds((prev) => {
        const next = new Set(prev);
        next.delete(registration.id);
        return next;
      });
    }
  }

  /**
   * Iterates over every recheckable row (pending / processing) and calls
   * checkOne for each. Sequential — Stripe's API tolerates parallel calls,
   * but sequential keeps the per-row spinner readable and avoids hammering
   * Mailjet if many rows flip to paid at once (each paid transition sends
   * buyer + N attendee emails). Refreshes summary at the end.
   */
  async function handleBulkCheck() {
    const targets = rows.filter((r) => RECHECKABLE_STATUSES.has(r.payment_status));
    if (targets.length === 0) {
      toast.info("Nenhuma inscrição pendente para verificar.");
      return;
    }
    setBulkChecking(true);
    setBulkProgress({ done: 0, total: targets.length });
    let flipped = 0;
    let errors = 0;
    for (let i = 0; i < targets.length; i++) {
      const updated = await checkOne(targets[i], { silent: true });
      if (!updated) errors += 1;
      else if (updated.status_changed) flipped += 1;
      setBulkProgress({ done: i + 1, total: targets.length });
    }
    setBulkChecking(false);
    // Refresh the summary tiles (paid / pending counts, revenue) — the rows
    // are already patched in place by checkOne.
    const sumResp = await getEventSummary(eventId);
    if (!(sumResp instanceof Error)) setSummary(sumResp.data);

    if (errors > 0) {
      toast.warning(
        `Verificadas ${targets.length} inscrições, ${flipped} atualizadas, ${errors} com erro.`
      );
    } else if (flipped > 0) {
      toast.success(
        `Verificadas ${targets.length} inscrições — ${flipped} mudaram de status.`
      );
    } else {
      toast.info(
        `Verificadas ${targets.length} inscrições, nenhuma teve mudança.`
      );
    }
  }

  const pendingCount = rows.filter((r) =>
    RECHECKABLE_STATUSES.has(r.payment_status)
  ).length;

  return (
    <div className="w-full max-w-[1400px] mx-auto px-6 pb-8">
      <div className="w-full flex items-center justify-between py-6 border-b border-slate-200 mb-6">
        <div className="flex flex-col">
          <button
            type="button"
            onClick={() => navigate("/tools/events")}
            className="text-slate-500 text-xs uppercase tracking-wider hover:text-blue-600 bg-transparent border-0 p-0 text-left"
          >
            ← Eventos
          </button>
          <h1 className="text-slate-900 text-2xl font-bold tracking-tight m-0">
            Inscrições ·{" "}
            <span className="font-mono text-blue-700">
              {event?.title || eventId}
            </span>
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="primary"
            onClick={handleBulkCheck}
            disabled={bulkChecking || pendingCount === 0}
            title={
              pendingCount === 0
                ? "Nenhuma inscrição pendente/em processamento"
                : `Verifica ${pendingCount} inscrição(ões) com status pendente/processando contra o Stripe`
            }
          >
            {bulkChecking ? (
              <>
                <Spinner
                  as="span"
                  size="sm"
                  animation="border"
                  role="status"
                  className="me-2"
                />
                Verificando {bulkProgress.done}/{bulkProgress.total}…
              </>
            ) : (
              <>
                Verificar pendentes
                {pendingCount > 0 && (
                  <Badge bg="light" text="dark" className="ms-2">
                    {pendingCount}
                  </Badge>
                )}
              </>
            )}
          </Button>
          <Button
            variant="outline-primary"
            onClick={() => navigate(`/tools/events/${eventId}/attendees`)}
          >
            Ver participantes
          </Button>
          <Button
            variant="outline-primary"
            onClick={() => navigate(`/tools/events/${eventId}/edit`)}
          >
            Editar evento
          </Button>
          <Button
            variant="outline-secondary"
            onClick={() => navigate("/tools/events")}
          >
            Voltar
          </Button>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <SummaryTile
            label="Inscrições"
            value={summary.registrations_total}
          />
          <SummaryTile label="Pagas" value={summary.registrations_paid} />
          <SummaryTile
            label="Pendentes"
            value={summary.registrations_pending}
          />
          <SummaryTile
            label="Receita paga"
            value={formatBRL(summary.revenue_cents_paid, summary.currency)}
          />
        </div>
      )}

      <Card className="p-4 border-0 shadow-sm mb-6">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex flex-col gap-1 min-w-[260px] flex-1">
            <label className="text-xs text-slate-500 font-semibold uppercase tracking-wider">
              Buscar (nome / e-mail do comprador)
            </label>
            <Form.Control
              type="text"
              value={filterSearch}
              onChange={(e) => setFilterSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleFilter();
              }}
            />
          </div>
          <div className="flex flex-col gap-1 min-w-[180px]">
            <label className="text-xs text-slate-500 font-semibold uppercase tracking-wider">
              Status
            </label>
            <Form.Select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </Form.Select>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <Button variant="primary" onClick={handleFilter}>
              Filtrar
            </Button>
            {(filterSearch || filterStatus) && (
              <Button variant="outline-secondary" onClick={handleClear}>
                Limpar
              </Button>
            )}
          </div>
        </div>
      </Card>

      <Card className="border-0 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
          <span className="text-sm font-semibold text-slate-700">
            {rows.length} {rows.length === 1 ? "inscrição" : "inscrições"}
          </span>
        </div>
        {loading ? (
          <div className="w-full text-center py-10">
            <Spinner />
          </div>
        ) : rows.length === 0 ? (
          <div className="w-full text-center py-10 text-slate-500">
            Nenhuma inscrição encontrada.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider w-12">
                    #
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Comprador
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Participantes
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Total
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Método
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Criada em
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Paga em
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => (
                  <tr
                    key={r.id}
                    className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                  >
                    <td className="px-4 py-3 text-sm text-slate-400 tabular-nums">
                      {idx + 1}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="font-semibold text-slate-900">
                        {r.buyer_name}
                      </div>
                      <div className="text-xs text-slate-500">
                        {r.buyer_email}
                      </div>
                      {r.buyer_tax_id && (
                        <div className="text-xs text-slate-400 font-mono">
                          {r.buyer_tax_id_type?.toUpperCase()}:{" "}
                          {r.buyer_tax_id}
                        </div>
                      )}
                      {r.buyer_company_name && (
                        <div className="text-xs text-slate-500">
                          {r.buyer_company_name}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700 tabular-nums">
                      {r.attendee_count}
                      {r.is_group && (
                        <Badge bg="info" className="ms-2 text-xs">
                          grupo
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-900 tabular-nums">
                      {formatBRL(r.total_cents, r.currency)}
                      {r.discount_cents > 0 && (
                        <div className="text-xs text-emerald-600">
                          −{formatBRL(r.discount_cents, r.currency)}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {paymentBadge(r.payment_status)}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {r.payment_method || "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">
                      {formatDate(r.created_at)}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">
                      {formatDate(r.paid_at)}
                    </td>
                    <td className="px-4 py-3 text-sm text-right whitespace-nowrap">
                      <RowCheckButton
                        registration={r}
                        loading={checkingIds.has(r.id)}
                        disabled={bulkChecking}
                        onCheck={() => checkOne(r)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

/**
 * Per-row "Verificar pagamento" button.
 *
 * Always shown so an admin can also re-confirm an already-paid row
 * (useful when ops suspects a row was marked paid by a coupon path
 * but the Stripe charge actually failed). The variant changes by
 * status so the eye is drawn to rows that actually need attention:
 *
 *   pending / processing → solid primary (clearly clickable)
 *   anything else        → outline secondary (still usable, less loud)
 *
 * Disabled while the bulk operation is running to avoid the user
 * triggering duplicate emails by clicking a row mid-bulk.
 */
function RowCheckButton({ registration, loading, disabled, onCheck }) {
  const needsCheck = RECHECKABLE_STATUSES.has(registration.payment_status);
  return (
    <Button
      size="sm"
      variant={needsCheck ? "primary" : "outline-secondary"}
      onClick={onCheck}
      disabled={loading || disabled}
      title={
        needsCheck
          ? "Reconsulta o status do pagamento no Stripe e envia o e-mail de confirmação se mudou para pago."
          : "Re-sincroniza essa inscrição com o Stripe (não envia novo e-mail se já estava paga)."
      }
    >
      {loading ? (
        <>
          <Spinner
            as="span"
            size="sm"
            animation="border"
            role="status"
            className="me-2"
          />
          Verificando…
        </>
      ) : (
        "Verificar"
      )}
    </Button>
  );
}

function SummaryTile({ label, value }) {
  return (
    <Card className="border-0 shadow-sm">
      <div className="px-4 py-3">
        <div className="text-xs text-slate-500 font-semibold uppercase tracking-wider">
          {label}
        </div>
        <div className="text-2xl font-bold text-slate-900 tabular-nums">
          {value}
        </div>
      </div>
    </Card>
  );
}
